import { useEffect, type RefObject } from 'react';

/**
 * Focus-trap reusable para modales del módulo. Captura `document.activeElement`
 * antes de montar (return-focus al cerrar), foca el primer elemento focusable
 * dentro del container y wrappea Tab/Shift+Tab cycle. En cleanup restaura el
 * foco al elemento previo (queueMicrotask para evitar race con el unmount del
 * DOM cuando el modal cierra y simultáneamente desmonta).
 *
 * Decisiones:
 * - Re-query de focusables en cada Tab keydown (NO cache al mount). Modales
 *   con controles disabled-dinámicos (ej. TransferStockModal: variant-select
 *   se habilita después de elegir producto) requieren la lista actualizada.
 *   Costo es despreciable — querySelector dentro de un container chico.
 * - Respeta autoFocus existente: si al montar `containerRef.current.contains(
 *   document.activeElement)` ya es true (porque algún input ya recibió foco
 *   por React's autoFocus prop), no sobrescribimos.
 * - Cross-iframe: el módulo va embebido en iframe Whatsfy. `document.active
 *   Element` referencia al document del iframe, no al top — correcto para
 *   nuestro caso (atrapamos foco solo dentro del iframe).
 * - Si no hay focusables (raro: modal solo con texto), warning a console y
 *   no crashea.
 *
 * Uso:
 *   const containerRef = useRef<HTMLDivElement>(null);
 *   useFocusTrap(containerRef, true);
 *   return <div className="modal" ref={containerRef}>...</div>;
 */

const FOCUSABLE_SELECTOR = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled]):not([type="hidden"])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(',');

function getFocusable(container: HTMLElement): HTMLElement[] {
  const nodes = container.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR);
  // offsetParent === null filtra elementos hidden (display:none / parent oculto).
  // No filtra visibility:hidden, pero el patrón actual del módulo no lo usa.
  return Array.from(nodes).filter((el) => el.offsetParent !== null);
}

export function useFocusTrap(
  containerRef: RefObject<HTMLElement>,
  active: boolean,
): void {
  useEffect(() => {
    if (!active) return;
    const container = containerRef.current;
    if (!container) return;

    // Guarda el activeElement previo (típicamente el botón que abrió el modal).
    const previouslyFocused =
      document.activeElement instanceof HTMLElement ? document.activeElement : null;

    // Si nadie dentro del container tiene foco aún, daleselo al primer focusable.
    // Si React's autoFocus ya posó foco dentro, respetamos.
    if (!container.contains(document.activeElement)) {
      const focusables = getFocusable(container);
      if (focusables.length > 0) {
        focusables[0].focus();
      } else {
        // Modal sin elementos focusables — no crashee, pero loggea: indica
        // problema de UX (el user queda con foco fuera del modal y no puede
        // cerrarlo con keyboard).
         
        console.warn('[useFocusTrap] No focusable elements inside modal container');
      }
    }

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'Tab') return;
      if (!container.contains(e.target as Node)) return;

      const focusables = getFocusable(container);
      if (focusables.length === 0) {
        e.preventDefault();
        return;
      }

      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      const activeEl = document.activeElement;

      if (e.shiftKey) {
        if (activeEl === first || !container.contains(activeEl)) {
          e.preventDefault();
          last.focus();
        }
      } else {
        if (activeEl === last) {
          e.preventDefault();
          first.focus();
        }
      }
    };

    container.addEventListener('keydown', handleKeyDown);

    return () => {
      container.removeEventListener('keydown', handleKeyDown);
      // queueMicrotask: si el modal se desmonta inmediato (ej. onClose síncrono
      // en submit), el DOM aún no procesó la remoción. Esperar al microtask
      // garantiza que .focus() corra después de que React commitee el unmount.
      queueMicrotask(() => {
        if (
          previouslyFocused &&
          previouslyFocused instanceof HTMLElement &&
          document.body.contains(previouslyFocused)
        ) {
          previouslyFocused.focus();
        }
      });
    };
  }, [containerRef, active]);
}
